/**
 * The player's phone (DESIGN §9): shift bar, phase screens, List / Board / Book / Team.
 */
import { useEffect, useRef, useState } from 'react';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import { draftEntry, type ActionCommand, type Affordance, type DraftView, type PatientView, type SeatCommand, type SeatView } from '@nightshift/engine/client';
import type { LiveSocket, Status } from '../net/socket.ts';
import { useNow, useSocket, useWakeLock } from '../net/hooks.ts';
import { linkValid } from '../net/api.ts';
import { linkProps } from '../app/router.ts';
import { Art, Countdown, PhaseGuide, Pips, phaseName, ROLE_TITLE, Secret, Toasts, useToasts } from '../ui/bits.tsx';
import { BoardTab } from './BoardTab.tsx';
import { BookTab } from './BookTab.tsx';
import { PatientCardCompact, PatientSheet, type Change } from './PatientCard.tsx';
import { TeamTab } from './TeamTab.tsx';
import { PhaseChanges } from './PhaseChanges.tsx';
import type { PracticeProps } from './Practice.tsx';

type FeedItem = { seq: number; type: string; turn: number; text: string; severity: number; patient?: string | null };

const PHASE_CUE: Record<string, string> = {
  ACT: 'Action. Look at new arrivals and plan your care.',
  LOG: 'Review. The turn has been played out. Read what happened and wait for Control.',
  HANDOVER: '0700. Hand over every patient aloud.',
  DEBRIEF: 'The shift is over.',
};
type SeatViewWire = (SeatView & { feed: FeedItem[] }) | { kind: 'unseated' };
type Tab = 'list' | 'board' | 'book' | 'team';
type DraftEdit = { id: string; cmd: ActionCommand; label: string; priority?: 1 | 2 | 3 }[];
type FailedDraft = { turn: number; reason: string; actions: DraftEdit | null };
type PendingDraft = { cmd: SeatCommand; actions: DraftEdit | null; acknowledged: boolean };
type DraftControls = {
  sending: boolean;
  failed: FailedDraft | null;
  send: (cmd: SeatCommand, actions?: DraftEdit) => Promise<boolean>;
  discard: () => void;
};

/** Recognize an acknowledged change in the latest authoritative boundary. */
export function draftReplyApplied(v: SeatView, cmd: SeatCommand): boolean {
  return !('expectedRevision' in cmd) || v.clock.turn !== cmd.turn || !!(v.draft && v.draft.revision > cmd.expectedRevision)
    || (!v.draft && (v.clock.phase === 'DEBRIEF' || v.clock.phase === 'ENDED'));
}

/** Keep mutation acknowledgement separate from the authoritative saved revision. */
function useDraftCommands(v: SeatView, sock: LiveSocket<SeatViewWire>, status: Status): DraftControls {
  const [pending, setPending] = useState<PendingDraft | null>(null);
  const [failed, setFailed] = useState<FailedDraft | null>(null);
  const locked = useRef(false);
  useEffect(() => {
    if (!pending?.acknowledged) return;
    const cmd = pending.cmd;
    if (draftReplyApplied(v, cmd)) {
      locked.current = false;
      setPending(null);
      setFailed(null);
    }
  }, [pending, v]);
  useEffect(() => {
    if (!pending?.acknowledged) return;
    const timeout = window.setTimeout(() => {
      const reason = 'No reply yet from the game. Check your saved plan before trying again.';
      console.warn('Draft acknowledgement missing its saved view', { command: pending.cmd.type, turn: v.clock.turn });
      setFailed({ turn: 'turn' in pending.cmd ? pending.cmd.turn : v.clock.turn, actions: pending.actions, reason });
      setPending(null);
      locked.current = false;
    }, 8000);
    return () => window.clearTimeout(timeout);
  }, [pending]);
  const send = async (cmd: SeatCommand, actions?: DraftEdit): Promise<boolean> => {
    if (locked.current) return false;
    locked.current = true;
    setPending({ cmd, actions: actions ?? null, acknowledged: false });
    try {
      const result = status === 'open' ? await sock.send(cmd) : { ok: false, reason: 'Reconnecting. Check your plan and try again once you are back online.' };
      if (!result.ok) throw new Error(result.reason ?? 'That change could not be saved.');
      setPending({ cmd, actions: actions ?? null, acknowledged: true });
      return true;
    } catch (error) {
      console.warn('Player change was not saved', { command: cmd.type, turn: v.clock.turn }, error);
      setFailed({ turn: 'turn' in cmd ? cmd.turn : v.clock.turn, actions: actions ?? null, reason: error instanceof Error ? error.message : 'That change could not be saved.' });
      setPending(null);
      locked.current = false;
      return false;
    }
  };
  return { sending: !!pending, failed, send, discard: () => setFailed(null) };
}

/** Announce observed changes without duplicating boundary receipts. */
function usePlayerFeed(v: SeatViewWire & SeatView, toast: (text: string, bad?: boolean) => void): void {
  const seen = useRef(0);
  useEffect(() => {
    const fresh = seen.current > 0 ? v.feed.filter(f => f.seq > seen.current) : [];
    for (const f of fresh) {
      if (f.type === 'DIED' || f.type === 'DETERIORATED') toast(f.text, true);
      else if (['COMPLICATION_LINE', 'EARLY_LINE', 'REFERRED', 'ROUTE_STARTED', 'CARD_OPENED', 'RED_BLANKET'].includes(f.type)) toast(f.text, f.severity >= 2);
    }
    seen.current = Math.max(1, seen.current, ...v.feed.map(f => f.seq));
  }, [v.feed, toast]);
}

/** Announce the two player-facing turn boundaries. */
function usePhaseCue(phase: SeatView['clock']['phase']): string | null {
  const [cue, setCue] = useState<string | null>(null);
  const previous = useRef(phase);
  useEffect(() => {
    if (previous.current === phase) return;
    previous.current = phase;
    setCue(PHASE_CUE[phase] ?? null);
    const timer = window.setTimeout(() => setCue(null), 2600);
    return () => window.clearTimeout(timer);
  }, [phase]);
  return cue;
}

/** Keep one seat's draft controls scoped to its connected player screen. */
export function ConnectedPlayer({ v, sock, status, identity }: { v: SeatView & { feed: FeedItem[] }; sock: LiveSocket<SeatViewWire>; status: Status; identity: string }): ReactElement {
  const [tab, setTab] = useState<Tab>('list');
  const [openId, setOpenId] = useState<string | null>(null);
  const [toasts, toast] = useToasts();
  const nextAction = useRef(0);
  const now = useNow(250) + sock.offset;
  const controls = useDraftCommands(v, sock, status);
  const cue = usePhaseCue(v.clock.phase);
  const active = !['SETUP', 'DEBRIEF', 'ENDED'].includes(v.clock.phase);
  useWakeLock(active);
  usePlayerFeed(v, toast);
  const blocked = status !== 'open' ? 'Reconnecting…' : controls.failed ? 'Check unsaved change' : undefined;
  const replace = (actions: DraftEdit) => controls.send({ type: 'SET_DRAFT', turn: v.clock.turn, expectedRevision: v.draft!.revision, actions: actions.map(draftEntry) }, actions);
  const act = async (a: Affordance): Promise<boolean> => {
    if (blocked || controls.sending) { toast(blocked ?? 'Wait for your saved plan.', true); return false; }
    if (a.cmd.type === 'OPEN_CARD' || a.cmd.type === 'CLOSE_CARD' || a.cmd.type === 'MARK_DISPUTED') return controls.send(a.cmd);
    if (!v.draft || v.clock.phase !== 'ACT' || v.clock.paused) { toast('You can change your plan only during the Action phase.', true); return false; }
    const ok = await replace([...v.draft.actions, { id: `${Date.now().toString(36)}-${++nextAction.current}`, cmd: a.cmd, label: a.label }]);
    if (!ok) toast('Your change was not saved. Close the card to check your plan.', true);
    return ok;
  };
  const all = [...v.beds.map(b => b.patient), ...v.corridor, ...v.opened];
  const openP = all.find(p => p?.id === openId) ?? null;
  const editable = status === 'open' && !controls.sending && v.clock.phase === 'ACT' && !v.clock.paused;
  return <div className={`player phase-${v.clock.phase}`}>
    <ShiftBar v={v} now={now} />
    {status !== 'open' && <div className="connbar">Reconnecting… your saved plan is safe. New changes wait until you reconnect.</div>}
    <div className="sr-only" aria-live="assertive" data-testid="phase-announce">{cue ?? ''}</div>
    {cue && !openP && <div className="phase-cue" aria-hidden>{cue}</div>}
    {v.broadcast && <div className="broadcast">{v.broadcast}</div>}
    <PhaseGuide phase={v.clock.phase} handoverTurn={v.clock.handoverTurn} />
    <main className="player-main">
      {v.clock.phase !== 'SETUP' && <PhaseChanges identity={identity} v={v} connected={status === 'open'} blocked={!!openP} />}
      {active && <DraftPlan v={v} send={controls.send} busy={controls.sending} blocked={blocked} />}
      {controls.failed && <DraftRecovery failed={controls.failed} editable={editable && controls.failed.turn === v.clock.turn} replace={replace} discard={controls.discard} />}
      <PlayerContent v={v} tab={tab} open={setOpenId} act={act} respond={controls.send} busy={!editable || !!blocked} />
    </main>
    {active && <nav className="tabbar" aria-label="Sections">
      {([['list', 'List', ListIcon], ['board', 'Board', BoardIcon], ['book', 'Book', BookIcon], ['team', 'Team', TeamIcon]] as const).map(([key, label, Icon]) => (
        <button key={key} className={tab === key ? 'on' : ''} onClick={() => setTab(key)} aria-current={tab === key ? 'page' : undefined} data-testid={`tab-${key}`}>
          <Icon /><span>{label}</span>{key === 'list' && v.prompts.length > 0 && <i className="dot" />}
        </button>
      ))}
    </nav>}
    <PatientSheet key={openP?.id ?? 'closed'} readOnly={!editable || !!blocked} p={openP} open={!!openP} onClose={() => setOpenId(null)} act={act} actionsLeft={v.clock.phase === 'ACT' ? v.draft?.remaining ?? null : null} />
    <Toasts list={toasts} />
  </div>;
}

/** Render the list, shared board, reference book or coordination panel. */
function PlayerContent({ v, tab, open, act, respond, busy }: { v: SeatView & { feed: FeedItem[] }; tab: Tab; open: (id: string) => void; act: (a: Affordance) => Promise<boolean>; respond: (cmd: SeatCommand) => Promise<boolean>; busy: boolean }): ReactElement {
  if (v.clock.phase === 'DEBRIEF' || v.clock.phase === 'ENDED') return <Debrief v={v} />;
  if (v.clock.phase === 'SETUP') return <PracticeLobby v={v} />;
  if (tab === 'board') return <BoardTab v={v} act={act} />;
  if (tab === 'book') return <BookTab v={v} />;
  if (tab === 'team') return <TeamTab v={v} act={act} respond={respond} busy={busy} />;
  return <ListTab v={v} feed={v.feed} open={open} act={act} />;
}

/** Present the saved plan and its explicit readiness separately. */
export function DraftPlan({ v, send, busy, blocked }: { v: SeatView; send: DraftControls['send']; busy: boolean; blocked?: string }): ReactElement | null {
  const draft = v.draft;
  if (!draft) return null;
  const editable = v.clock.phase === 'ACT' && !v.clock.paused && !busy && !blocked;
  const status = blocked ?? (v.clock.phase !== 'ACT' ? 'Turn ended' : busy ? 'Saving…' : draft.ready ? 'Ready' : 'Saved');
  const replace = (actions: DraftEdit) => send({ type: 'SET_DRAFT', turn: draft.turn, expectedRevision: draft.revision, actions: actions.map(draftEntry) }, actions);
  return (
    <section className="lead-tools" aria-label="Your turn plan" data-testid="draft-plan">
      <div className="row wrap"><h2 className="sec-title">Your turn plan</h2><strong role="status" data-testid="draft-status">{status}</strong></div>
      <p>{draft.proposedCost} actions proposed · {draft.remaining} left to plan · {v.me.actions.spent} spent</p>
      <p className="muted">You can change your plan until the turn ends. Ready tells your team you have finished. When Control ends the turn or time runs out, every saved plan goes ahead, Ready or not.</p>
      {v.clock.paused && <p role="status">Control has paused the turn. Your plan is read-only.</p>}
      <DraftActions actions={draft.actions} disabled={!editable} replace={replace} />
      <button className="btn btn-primary" disabled={!editable} data-testid="draft-ready" onClick={() => send({ type: 'SET_READY', turn: draft.turn, expectedRevision: draft.revision, ready: !draft.ready })}>
        {draft.ready ? 'Keep planning' : 'Ready'}
      </button>
    </section>
  );
}

/** Keep clinical order editable only inside its fixed resolution stage. */
function DraftActions({ actions, disabled, replace }: { actions: DraftView['actions']; disabled: boolean; replace: (actions: DraftEdit) => Promise<boolean> }): ReactElement {
  const stages = { LOGISTICS: 'Resources', CLINICAL: 'Care', DEPARTURE: 'Discharges', TRANSFER: 'Referrals' } as const;
  const move = (index: number, direction: number) => {
    const same = actions.map((a, i) => a.stage === actions[index]!.stage ? i : -1).filter(i => i >= 0);
    const other = same[same.indexOf(index) + direction];
    if (other === undefined) return;
    const next = [...actions];
    [next[index], next[other]] = [next[other]!, next[index]!];
    void replace(next);
  };
  if (actions.length === 0) return <p className="muted">No actions planned. Choose actions from your patient cards, Board or Team.</p>;
  const scanNote = actions.find(a => a.note)?.note;
  return <>
    {scanNote && <p className="muted" data-testid="scan-note">{scanNote}</p>}
    <p className="muted">Resources happen first, then care, discharges and referrals. Use Earlier and Later to set the order within each group. You see results when the turn ends; empirical treatment you chose is not undone by them.</p>
    {(Object.keys(stages) as (keyof typeof stages)[]).filter(stage => actions.some(a => a.stage === stage)).map(stage => (
      <section key={stage} aria-label={stages[stage]}>
        <h3 className="sec-title">{stages[stage]}</h3>
        <ol className="why-list">{actions.filter(a => a.stage === stage).map((a, position, group) => (
          <li key={a.id}>
            <span>{a.label} · {a.cost} action{a.cost === 1 ? '' : 's'}</span>
            {a.scanner && <label className="alloc-row"><span className="alloc-label">Scan priority</span>
              <select className="input alloc-select" value={a.priority ?? 2} disabled={disabled} data-testid={`scan-priority-${a.id}`}
                onChange={event => void replace(actions.map(x => x.id === a.id ? { ...x, priority: Number(event.target.value) as 1 | 2 | 3 } : x))}>
                <option value={3}>High</option><option value={2}>Normal</option><option value={1}>Low</option>
              </select></label>}
            {a.reason && <p className="muted">May not proceed: {a.reason}</p>}
            <div className="row wrap">
              <button className="btn btn-small" disabled={disabled || position === 0} aria-label={`Move ${a.label} earlier`} onClick={() => move(actions.indexOf(a), -1)}>Earlier</button>
              <button className="btn btn-small" disabled={disabled || position === group.length - 1} aria-label={`Move ${a.label} later`} onClick={() => move(actions.indexOf(a), 1)}>Later</button>
              <button className="btn btn-small" disabled={disabled} aria-label={`Remove ${a.label}`} onClick={() => replace(actions.filter(x => x.id !== a.id))}>Remove</button>
            </div>
          </li>
        ))}</ol>
      </section>
    ))}
  </>;
}

/** Preserve a rejected edit for deliberate review before replacing the saved plan. */
function DraftRecovery({ failed, editable, replace, discard }: { failed: FailedDraft; editable: boolean; replace: (actions: DraftEdit) => Promise<boolean>; discard: () => void }): ReactElement {
  return <section className="lead-tools" role="alert" data-testid="draft-recovery">
    <h2 className="sec-title">Your change was not saved</h2>
    <p>{failed.reason}</p>
    {failed.actions && <><p>Your attempted plan for turn {failed.turn}:</p><ol>{failed.actions.map(a => <li key={a.id}>{a.label}</li>)}</ol></>}
    <p>Check your saved plan above before replacing it. Nothing is retried automatically.</p>
    {!editable && <p>You cannot use this plan while the turn is paused or over, or while you are offline.</p>}
    <div className="row wrap">
      {failed.actions && <button className="btn btn-primary" disabled={!editable} onClick={() => replace(failed.actions!)}>Use this plan instead</button>}
      <button className="btn btn-quiet" onClick={discard}>Discard this attempt</button>
    </div>
  </section>;
}

export function PlayerApp({ token, sessionId, onLeave }: { token: string; sessionId: string; onLeave: () => void }) {
  const { sock, view, status } = useSocket<SeatViewWire>(`/ws?t=${encodeURIComponent(token)}`, () => linkValid(token));
  if (status === 'denied') return <Gone onLeave={onLeave} reason={sock?.reason} />;
  if (!view || !sock) return <Splash text={status === 'open' ? 'Loading your list…' : 'Connecting…'} />;
  if (view.kind === 'unseated') return <Gone onLeave={onLeave} reason={sock.reason} />;
  const identity = `${sessionId}:${view.me.table}:${view.me.role}`;
  return <ConnectedPlayer key={`${token}:${identity}`} v={view} sock={sock} status={status} identity={identity} />;
}

// ───────────────────────────── shift bar ─────────────────────────────

function ShiftBar({ v, now }: { v: SeatView; now: number }) {
  const [legend, setLegend] = useState(false);
  const total = v.me.actions.available + v.me.actions.spent;
  const remaining = v.clock.phase === 'ACT' ? Math.max(0, v.draft?.remaining ?? v.me.actions.available) : v.me.actions.available;
  return (
    <header className={`shiftbar spec-${v.me.role}`}>
      <div className="sb-row">
        <span className="sb-hour display">{v.clock.hour}</span>
        <span className="sb-turn">
          {v.clock.turn > 0 ? `Turn ${v.clock.turn} of ${v.clock.lastTurn}` : 'Before the shift'}
          <span className="sb-phase">{phaseName(v.clock.phase)}</span>
        </span>
        <Countdown endsAt={v.clock.phaseEndsAt} remainingMs={v.clock.remainingMs} paused={v.clock.paused} now={now} total={v.clock.phaseMs ?? undefined} />
      </div>
      <div className="sb-row sb-me">
        <span className="sb-role">
          <span className="spec-dot" />
          {v.me.title}
          {v.me.isLead && <span className="lead-badge">Lead</span>}
          <span className="sb-table">Table {v.me.table}</span>
        </span>
        <button type="button" className="sb-stats" aria-expanded={legend} aria-label="What these mean" onClick={() => setLegend((x) => !x)} data-testid="sb-stats">
          <Pips available={remaining} spent={total - remaining} />
        </button>
      </div>
      {legend && (
        <p className="sb-legend" data-testid="sb-legend">
          {total > 0 ? `${remaining} of ${total} actions ${v.clock.phase === 'ACT' ? 'left to plan' : 'unused'}` : 'You get your actions when the Action phase starts'}. {v.me.actions.spent} actions used so far.
        </p>
      )}
    </header>
  );
}

// ───────────────────────────── list ─────────────────────────────

/** Keep review and handover notices beside the affected patients. */
function ListNotices({ v, worse, hasFeed }: { v: SeatView; worse: PatientView[]; hasFeed: boolean }): ReactElement {
  const handover = v.clock.phase === 'HANDOVER';
  const tick = v.clock.phase === 'LOG';
  return <>
      {handover && (
        <p className="phase-note">
          It is 0700. Hand over each patient aloud to {v.board.dayTeam ? <strong>{v.board.dayTeam.name}</strong> : 'the day team'}{v.board.dayTeam ? ` (${v.board.dayTeam.line})` : ''}. Each patient counts as handed over by how far through their treatment they are; what is left counts as harm, more for a Red than a Green, and more for a hard-to-please consultant.
        </p>
      )}
      {tick && (
        <p className="phase-note">
          Read your results and see who got worse. Control starts the next turn.
          {worse.length > 0 ? (
            <strong className="phase-note-worse"> Worse: {worse.map((p) => p.name).join(', ')}.</strong>
          ) : hasFeed ? (
            <span className="muted"> Nobody on your list got worse.</span>
          ) : null}
        </p>
      )}
  </>;
}

/** Keep event choices available as explicit entries in the saved plan. */
function SeatPrompts({ v, act }: { v: SeatView; act: (a: Affordance) => Promise<boolean> }): ReactElement | null {
  return <>
      {v.prompts.length > 0 && (
        <section className="prompts inline">
          {v.prompts.map((q) => (
            <p key={q.id}>{q.text}</p>
          ))}
          <div className="prompt-actions">
            {v.seatActions
              .filter((a) => a.group === 'prompt' && a.enabled)
              .map((a) => (
                <button key={a.id} className="btn btn-primary btn-small" onClick={() => act(a)} data-testid={`act-${a.id}`}>
                  {a.label}
                </button>
              ))}
          </div>
        </section>
      )}
  </>;
}

/** Group shared interruptions and service actions without hiding their cost. */
function SeatTasks({ v, act }: { v: SeatView; act: (a: Affordance) => Promise<boolean> }): ReactElement {
  const seat = v.seatActions.filter(a => a.group !== 'lead' && a.group !== 'layer' && a.cmd.type !== 'LEND_STAFF' && a.group !== 'prompt');
  return <>
      {v.interrupts.length > 0 && (
        <section className="interrupts">
          {v.interrupts.map((i) => (
            <div key={i.id} className={`interrupt ${i.state}`}>
              <span>
                <strong>{i.title}</strong>
                <span className="muted"> {i.state === 'ESCALATED' ? 'ignored too long: it will use up one of your actions' : i.state === 'PARKED' ? `waiting ${i.age} turn${i.age === 1 ? '' : 's'}` : 'new'}</span>
              </span>
            </div>
          ))}
          <div className="row wrap">
            {v.seatActions
              .filter((a) => a.cmd.type === 'RESOLVE_INTERRUPT')
              .map((a) => (
                <button key={a.id} className="btn btn-small" disabled={!a.enabled} onClick={() => act(a)} data-testid={`act-${a.id}`}>
                  {a.label} (costs 1 action)
                </button>
              ))}
          </div>
        </section>
      )}
      {seat.filter((a) => a.cmd.type !== 'RESOLVE_INTERRUPT').length > 0 && (
        <div className="row wrap seat-acts">
          {seat
            .filter((a) => a.cmd.type !== 'RESOLVE_INTERRUPT')
            .map((a) => (
              <button key={a.id} className={`btn btn-small ${a.enabled ? 'btn-primary' : ''}`} disabled={!a.enabled} onClick={() => act(a)} title={a.reason} data-testid={`act-${a.id}`}>
                {a.label}
              </button>
            ))}
        </div>
      )}
  </>;
}

/** Keep beds, overflow and shared cards readable while the plan is locked. */
function PatientLists({ v, changes, open }: { v: SeatView; changes: Map<string, Change[]>; open: (id: string) => void }): ReactElement {
  return <>
      <section className="beds" aria-label="Beds">
        {v.beds.map((b) =>
          b.patient ? (
            <PatientCardCompact key={b.index} p={b.patient} onOpen={() => open(b.patient!.id)} here={b.label} changes={changes.get(b.patient.id)} />
          ) : (
            <div key={b.index} className={`bed-empty ${b.disabled ? 'closed' : ''}`}>
              <span>{b.label}</span>
              <span className="muted">{b.disabled ? 'closed this turn' : 'empty'}</span>
            </div>
          ),
        )}
      </section>
      {v.corridor.length > 0 && (
        <section className="corridor" aria-label="Corridor">
          <h3 className="sec-title">Corridor: no bed free</h3>
          {v.corridor.map((p) => (
            <PatientCardCompact key={p.id} p={p} onOpen={() => open(p.id)} here="Corridor" changes={changes.get(p.id)} />
          ))}
        </section>
      )}
      {v.opened.length > 0 && (
        <section aria-label="Opened to you">
          <h3 className="sec-title">Opened to you</h3>
          {v.opened.map((p) => (
            <PatientCardCompact key={p.id} p={p} onOpen={() => open(p.id)} here={`${p.holder ? ROLE_TITLE[p.holder] : ''}: ${p.where}`} changes={changes.get(p.id)} />
          ))}
        </section>
      )}
  </>;
}

/** What happened to each patient this turn, from the card itself and this seat's feed. */
export function changesThisTurn(v: SeatView, feed: FeedItem[]): Map<string, Change[]> {
  const out = new Map<string, Change[]>();
  const add = (id: string, c: Change) => {
    const l = out.get(id) ?? [];
    if (!l.includes(c)) l.push(c);
    out.set(id, l);
  };
  const turn = v.clock.turn;
  const mine = [...v.beds.map((b) => b.patient).filter((x): x is PatientView => !!x), ...v.corridor, ...v.opened];
  for (const p of mine) {
    if (p.flags.arrivedThisTurn) add(p.id, 'new');
    if (p.revealed.some((r) => r.turn === turn)) add(p.id, 'line');
    if (p.flags.dischargeNotBeforeTurn !== null && p.flags.dischargeNotBeforeTurn > turn) add(p.id, 'delayed');
  }
  const ids = new Set(mine.map((p) => p.id));
  const boundaryEvents = v.receipts.flatMap(receipt => receipt.events.map(event => ({ ...event, turn: receipt.turn })));
  for (const f of [...feed, ...boundaryEvents]) {
    if (f.turn !== turn || !f.patient || !ids.has(f.patient)) continue;
    if (f.type === 'TRANSFER_ARRIVED') add(f.patient, 'new');
    else if (f.type === 'DETERIORATED' || f.type === 'HARMED') add(f.patient, 'worse');
    else if (f.type === 'REFERRED' && mine.find((p) => p.id === f.patient)?.flags.referredBy) add(f.patient, 'referred');
  }
  return out;
}

export function ListTab({ v, feed, open, act }: { v: SeatView; feed: FeedItem[] | null; open: (id: string) => void; act: (a: Affordance) => Promise<boolean> }) {
  const changes = changesThisTurn(v, feed ?? []);
  const all = [...v.beds.map(b => b.patient).filter((p): p is PatientView => !!p), ...v.corridor];
  const worse = all.filter(p => changes.get(p.id)?.includes('worse'));
  return <div className="list">
    <ListNotices v={v} worse={worse} hasFeed={feed !== null} />
    <SeatPrompts v={v} act={act} />
    <SeatTasks v={v} act={act} />
    <PatientLists v={v} changes={changes} open={open} />
  </div>;
}

// ───────────────────────────── lobby & debrief ─────────────────────────────

const TURN_STEPS: [string, string][] = [
  ['Action', 'Look at new patients and events, plan your care and press Ready.'],
  ['Review', 'Everyone’s plans are played out together. Read what worked, what could not be done and which referrals are waiting.'],
  ['Next turn', 'Control starts the next turn. Referred patients and new arrivals reach their teams.'],
];

/** Load practice only on request while the live lobby remains mounted. */
function PracticeLobby({ v }: { v: SeatView }): ReactElement {
  const [requested, setRequested] = useState(false);
  const [Practice, setPractice] = useState<ComponentType<PracticeProps> | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!requested) return;
    let cancelled = false;
    void import('./Practice.tsx').then(({ Practice: loaded }) => {
      if (!cancelled) setPractice(() => loaded);
    }).catch((error: unknown) => {
      console.warn('Private practice failed to load', error);
      if (!cancelled) {
        setError('Practice could not load. Try again, or carry on in the lobby.');
        setRequested(false);
      }
    });
    return () => { cancelled = true; };
  }, [requested]);
  const done = () => { setRequested(false); setPractice(null); setError(null); };
  if (Practice) return <Practice role={v.me.role} onDone={done} />;
  return (
    <Lobby v={v}>
      {error && <p role="alert" data-testid="practice-error">{error}</p>}
      <button className="btn btn-primary" data-testid="practice-start" disabled={requested} onClick={() => { setError(null); setRequested(true); }}>
        Practice while you wait
      </button>
      <a className="btn" {...linkProps('/briefing')} data-testid="briefing-link-lobby">About the hospital</a>
      {requested && <p role="status" data-testid="practice-loading">Loading private practice… <button className="btn btn-quiet" onClick={done}>Cancel</button></p>}
    </Lobby>
  );
}

function Lobby({ v, children }: { v: SeatView; children?: ReactNode }) {
  const seats = v.board.seats;
  return (
    <main className="lookup lobby-player">
      <p className="lookup-kicker">You are seated</p>
      <p className="lookup-hour display">2100</p>
      <p className="huddle-line display">
        {v.me.title}, Table {v.me.table}
      </p>
      <p className="lookup-sub">Control will start the shift. Your phone is your screen: keep it to yourself.</p>
      {children}
      {v.me.objective && (
        <Secret label="Show my secret objective" testId="secret-objective">
          <article className="objective">
            <span className="muted">Your secret objective. Keep it to yourself.</span>
            <h3>{v.me.objective.title}</h3>
            <p>{v.me.objective.text}</p>
          </article>
        </Secret>
      )}
      <section className="lobby-roster" aria-label={`Table ${v.me.table}`}>
        <h2 className="sec-title">Your table</h2>
        {seats.length > 0 ? (
          <ul>
            {seats.map((s) => (
              <li key={s.role} className={`spec-${s.role} ${s.role === v.me.role ? 'me' : ''}`}>
                <span className="spec-dot" />
                <strong>{ROLE_TITLE[s.role]}</strong>
                <span className="muted">{s.role === v.me.role ? 'you' : s.bot ? 'bot' : (s.name ?? 'seated')}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Waiting for teammates.</p>
        )}
      </section>
      <section className="turn-primer" aria-label="How a turn goes">
        <h2 className="sec-title">How a turn goes</h2>
        <ol>
          {TURN_STEPS.map(([k, t]) => (
            <li key={k}>
              <strong>{k}</strong>
              <span>{t}</span>
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}

/** Explain the cards whose final diagnosis differed from their presentation. */
function DebriefTraps({ traps }: { traps: NonNullable<SeatView['debrief']>['traps'] }): ReactElement | null {
  return <>
      {traps.length > 0 && (
        <section>
          <h2 className="display" style={{ fontSize: 'var(--t-20)', margin: '18px 0 8px' }}>
            Cards that were something else
          </h2>
          {traps.map((t, i) => (
            <div key={i} className="trap">
              <p>
                <strong>{t.name}</strong> arrived as {t.provisional}; it was {t.truth}.
              </p>
              {t.bias.length > 0 && (
                <p className="trap-bias">
                  <strong>{t.bias[0]}.</strong> {t.bias[1]}
                </p>
              )}
            </div>
          ))}
        </section>
      )}
  </>;
}

function Debrief({ v }: { v: SeatView }) {
  const d = v.debrief;
  if (!d) return <Splash text="The shift is over." />;
  const big: [string, number, string][] = [
    ['saved', d.dashboard.saved, 'ok'],
    ['harmed', d.dashboard.harmed, d.dashboard.harmed ? 'warn' : ''],
    ['died', d.dashboard.died, d.dashboard.died ? 'bad' : ''],
  ];
  const rest = (
    [
      ['handed over at 0700', d.dashboard.handedOver],
      ['sent home early', d.dashboard.early],
      ['bounced back', d.dashboard.bounceBacks],
      ['diverted away', d.dashboard.diverted],
      ['received from elsewhere', d.dashboard.accepted],
      [d.dashboard.overflowDelays === 1 ? 'extra stay from workload' : 'extra stays from workload', d.dashboard.overflowDelays],
    ] as [string, number][]
  ).filter(([, n]) => n > 0);
  return (
    <main className="debrief-player">
      <Art name={v.clock.handoverTurn ? 'dawn' : 'hero'} className="debrief-art" eager />
      <p className="lookup-kicker">{v.clock.handoverTurn ? '0700. The day team has the building.' : `Control ended the shift at ${v.clock.hour}.`}</p>
      <h1 className="display">Table {v.me.table}</h1>
      <div className="dash-big" data-testid="debrief-outcome">
        {big.map(([k, n, tone]) => (
          <div key={k} className={`dash-big-cell ${tone}`}>
            <span className="dash-n display">{n}</span>
            <span className="dash-k">{k}</span>
          </div>
        ))}
      </div>
      {rest.length > 0 && <p className="dash-rest">{rest.map(([k, n]) => `${n} ${k}`).join(' · ')}</p>}
      {d.objective && (
        <article className={`objective ${d.objective.achieved ? 'won' : ''}`}>
          <span className="muted">Your objective {d.objective.achieved ? 'achieved' : 'not achieved'}</span>
          <h3>{d.objective.title}</h3>
          <p>{d.objective.detail}</p>
        </article>
      )}
      <DebriefTraps traps={d.traps} />
      <p><a className="muted" {...linkProps('/briefing')} data-testid="briefing-link-debrief">About the hospital</a></p>
    </main>
  );
}

export function Splash({ text }: { text: string }) {
  return (
    <main className="splash">
      <p className="lookup-hour display">2100</p>
      <p className="muted">{text}</p>
    </main>
  );
}

function Gone({ onLeave, reason }: { onLeave: () => void; reason?: string | null }) {
  return (
    <main className="splash">
      <p className="huddle-line display" role="alert">{reason ?? 'Your seat was given to another device.'}</p>
      {!reason && <p className="muted">Ask Control to give it back, or join again.</p>}
      <button className="btn btn-primary" onClick={onLeave}>
        Join again
      </button>
    </main>
  );
}

// ───────────────────────────── icons ─────────────────────────────

function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      <rect x="4" y="3" width="16" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function BoardIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      <rect x="3" y="4" width="8" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <rect x="13" y="4" width="8" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <rect x="3" y="13" width="18" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}
function BookIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      <path d="M5 4h9a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5 17a3 3 0 0 1 3-3h9" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}
function TeamIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      <circle cx="9" cy="9" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17" cy="10" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0M14 19a4.2 4.2 0 0 1 7 -3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
